import { useEffect, useState, lazy, Suspense } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, addMonths, parse } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { dateFnsLocale } from "../utils/dateLocale";
import { Box } from "@mui/system";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import MapIcon from "@mui/icons-material/Map";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import { ITrainingSchedule, TimeSlot } from "../definitions";
import { v4 } from "uuid";
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { reverseGeocode, formatCoords } from '../utils/geocoding';

// Lazy load MapPickerDialog (contains mapbox-gl)
const MapPickerDialog = lazy(() => import("./MapDialog"));

export function convertDayToLabel(day: number): string {
  const dayMap: { [key: number]: string } = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
  };
  return dayMap[day] || "Unknown";
}

export const weekDays = [
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
  { label: "Sunday", value: 0 },
];

const DEFAULT_START_TIME = "18:00";
const DEFAULT_END_TIME = "20:00";

interface TrainingScheduleDialogProps {
  open: boolean;
  initialData?: ITrainingSchedule;
  onClose: () => void;
  onSave: (data: ITrainingSchedule) => void;
}

export default function TrainingScheduleDialog({
  open,
  initialData,
  onClose,
  onSave,
}: TrainingScheduleDialogProps) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(addMonths(new Date(), 1));
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [locationError, setLocationError] = useState<string | null>(null);

  const resetState = () => {
    setName("");
    setLocation("");
    setShowMap(false);
    setStartDate(new Date());
    setEndDate(addMonths(new Date(), 1));
    setSelectedDays([]);
    setSlots([]);
    setLocationError(null);
  };

  // Prefill fields when editing; reset when creating a new schedule.
  useEffect(() => {
    if (!open) return;

    if (!initialData) {
      resetState();
      return;
    }

    const parsedStart = parse(initialData.startDate, "yyyyMMdd", new Date());
    const parsedEnd = parse(initialData.endDate, "yyyyMMdd", new Date());

    setName(initialData.name ?? "");
    setLocation(initialData.location ?? "");
    setShowMap(false);
    setStartDate(isNaN(parsedStart.getTime()) ? new Date() : parsedStart);
    setEndDate(isNaN(parsedEnd.getTime()) ? addMonths(new Date(), 1) : parsedEnd);
    setSlots((initialData.slots ?? []).map(slot => ({ ...slot, id: slot.id ?? v4() })));
    setSelectedDays(Array.from(new Set((initialData.slots ?? []).map(slot => slot.day))));
    setLocationError(null);
  }, [open, initialData]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDayToggle = (day: number) => {
    const isCurrentlySelected = selectedDays.includes(day);

    setSelectedDays((prev) =>
      isCurrentlySelected ? prev.filter((d) => d !== day) : [...prev, day]
    );

    setSlots((prev) => {
      if (isCurrentlySelected) {
        return prev.filter((slot) => slot.day !== day);
      }

      const existing = prev.find((slot) => slot.day === day);
      if (existing) {
        return prev;
      }

      return [
        ...prev,
        {
          id: v4(),
          day,
          startTime: DEFAULT_START_TIME,
          endTime: DEFAULT_END_TIME,
        },
      ];
    });
  };

  const handleTimeChange = (
    slotId: string,
    field: "startTime" | "endTime",
    value: string
  ) => {
    setSlots((prev) => {
      const idx = prev.findIndex((slot) => slot.id === slotId);
      if (idx !== -1) {
        const updated = [...prev];
        const existing = updated[idx];
        const nextSlot = {
          ...existing,
          [field]: value,
        };

        if (!nextSlot.startTime) {
          nextSlot.startTime = DEFAULT_START_TIME;
        }
        if (!nextSlot.endTime) {
          nextSlot.endTime = DEFAULT_END_TIME;
        }

        updated[idx] = nextSlot;
        return updated;
      }
      return prev;
    });
  };

  const handleAddSlot = (day: number) => {
    setSlots((prev) => [
      ...prev,
      { id: v4(), day, startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME },
    ]);
  };

  const handleRemoveSlot = (slotId: string, day: number) => {
    const remainingForDay = slots.filter((slot) => slot.day === day && slot.id !== slotId).length;
    setSlots((prev) => prev.filter((slot) => slot.id !== slotId));
    if (remainingForDay === 0) setSelectedDays((days) => days.filter((selectedDay) => selectedDay !== day));
  };

  const handleUseCurrentLocation = async () => {
    try {
      setLocationError(null);
      const position = await Geolocation.getCurrentPosition();
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const resolvedAddress = await reverseGeocode(lat, lng);

      if (resolvedAddress) {
        setLocation(resolvedAddress);
        setLocationError(null);
      } else {
        const coords = formatCoords(lat, lng);
        setLocation(coords);
        setLocationError(`Geocoding service unavailable. Using coordinates: ${coords}`);
      }
    } catch (error: any) {
      const errorMessages: Record<string, string> = {
        'User denied Geolocation': "Location permission denied. Please enable location access and try again.",
        'location unavailable': "Location information unavailable. Please try again.",
        'timeout': "Location request timed out. Please try again."
      };
      setLocationError(errorMessages[error.message] || "Unable to retrieve your location. Please try again.");
    }
  };

  const handleMapSelect = (lat: number, lng: number, address: string) => {
    setLocation(address);
    setShowMap(false);
  };

  const handleSave = () => {
    if (!name || !startDate || !endDate || !location || slots.length === 0) return;

    // Validate that all selected days have complete time slots
    const validSlots = slots.filter((slot) =>
      selectedDays.includes(slot.day) &&
      slot.startTime &&
      slot.endTime
    );

    if (selectedDays.some((day) => !validSlots.some((slot) => slot.day === day))) {
      alert("Please add a complete time slot for every selected day.");
      return;
    }

    onSave({
      ...(initialData ?? {}),
      id: initialData?.id ?? v4(),
      name,
      startDate: format(startDate, "yyyyMMdd"),
      endDate: format(endDate, "yyyyMMdd"),
      location,
      slots: validSlots.map((slot) => ({ ...slot, id: slot.id ?? v4() })),
      attending: initialData?.attending ?? [],
      notAttending: initialData?.notAttending ?? [],
      attendanceOverrides: initialData?.attendanceOverrides ?? {},
      attendanceOverridesBySlot: initialData?.attendanceOverridesBySlot ?? {},
    });
    resetState();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{initialData ? 'Edit Training/Event Schedule' : 'Define Recurring Training/Event Schedule'}</DialogTitle>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
          <DialogContent>
            <TextField
              label="Event Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              fullWidth
              margin="normal"
              error={!!locationError}
              helperText={locationError}
              InputProps={{
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Use current location"
                        onClick={handleUseCurrentLocation}
                        edge="end"
                        size="small"
                      >
                        <MyLocationIcon />
                      </IconButton>
                    </InputAdornment>
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Find on map"
                        onClick={() => setShowMap(true)}
                        edge="end"
                        size="small"
                      >
                        <MapIcon />
                      </IconButton>
                    </InputAdornment>
                  </>
                ),
              }}
            />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
            <Typography sx={{ mt: 2, mb: 1 }}>Recurring Days & Times</Typography>
            <Grid
              container
              spacing={1}
              direction="row"
              sx={{ display: "flex", flexDirection: "column" }}
            >
              {weekDays.map((day) => (
                <Box key={day.value}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDays.includes(day.value)}
                        onChange={() => handleDayToggle(day.value)}
                      />
                    }
                    label={day.label}
                  />
                  {selectedDays.includes(day.value) && (
                    <Box sx={{ pl: 3, display: "flex", flexDirection: "column", gap: 1 }}>
                      {slots.filter((slot) => slot.day === day.value).map((slot) => (
                        <Grid container spacing={1} alignItems="center" key={slot.id}>
                          <Grid>
                            <TextField
                              label="Start Time"
                              type="time"
                              size="small"
                              value={slot.startTime || DEFAULT_START_TIME}
                              onChange={(e) => handleTimeChange(slot.id ?? "", "startTime", e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                            />
                          </Grid>
                          <Grid>
                            <TextField
                              label="End Time"
                              type="time"
                              size="small"
                              value={slot.endTime || DEFAULT_END_TIME}
                              onChange={(e) => handleTimeChange(slot.id ?? "", "endTime", e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{ step: 300 }}
                            />
                          </Grid>
                          <Grid>
                            <IconButton
                              aria-label={`Remove ${day.label} session ${slot.startTime}`}
                              onClick={() => handleRemoveSlot(slot.id ?? "", day.value)}
                              size="small"
                            >
                              <RemoveCircleOutline />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                      <Button
                        startIcon={<AddCircleOutline />}
                        onClick={() => handleAddSlot(day.value)}
                        size="small"
                        sx={{ alignSelf: "flex-start" }}
                      >
                        Add session
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
            </Grid>
          </DialogContent>
        </LocalizationProvider>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!name || !location || !startDate || !endDate || selectedDays.length === 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {showMap && (
        <Suspense fallback={null}>
          <MapPickerDialog
            onSelect={handleMapSelect}
            onClose={() => setShowMap(false)}
          />
        </Suspense>
      )}
    </>
  );
}
